import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus } from 'lucide-react';
import { useUsers } from '@/hooks/useUsers';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export const CreateUserDialog = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'staff',
  });

  const { inviteUser, isInviting } = useUsers();
  const { userRole } = useRoleAccess();
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.role || !user) {
      return;
    }

    try {
      // Get current user's name and tenant info
      const { data: userData } = await supabase
        .from('users_public')
        .select('name')
        .eq('id', user.id)
        .single();

      const { data: tenantData } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .single();

      inviteUser({
        name: formData.name,
        email: formData.email,
        role: formData.role,
        tenantId: tenantData?.tenant_id || '',
        inviterName: userData?.name || user.email || 'System Administrator'
      });

      setFormData({ name: '', email: '', role: 'staff' });
      setOpen(false);
    } catch (error) {
      console.error('Error preparing invitation:', error);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Only show if user has permission to create users (owner, admin, or manager)
  if (!['owner', 'admin', 'manager'].includes(userRole)) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="cta-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add New User
        </Button>
      </DialogTrigger>
      <DialogContent className="glass-panel border-0">
        <DialogHeader>
          <DialogTitle>Invite New User</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Enter user's full name"
              className="glass-input"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="Enter user's email"
              className="glass-input"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
              <SelectTrigger className="glass-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass-panel border-0">
                <SelectItem value="staff">Staff Member</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                {userRole === 'owner' && (
                  <>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setOpen(false)}
              className="glass-button"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isInviting}
              className="cta-primary"
            >
              {isInviting ? 'Inviting...' : 'Send Invitation'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};